import React, { useState, useEffect } from 'react';
import { X, Save, Package, Shield, Plus, Trash2 } from 'lucide-react';
import { assetService } from '../../../services/assetService';
import { userProfileService } from '../../../services/userProfileService';

export default function AddAssetModal({ isOpen, onClose, onAssetAdded, editingAsset }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userCurrency, setUserCurrency] = useState('USD');

  const [formData, setFormData] = useState({
    assetName: '',
    assetType: 'computer',
    assetValue: '',
    location: 'in_office',
    assignedStaffMember: '',
    serialNumber: '',
    warrantyExpiry: '',
    specifications: {},
    isInsured: false,
    purchaseDate: '',
    notes: '',
    otherAssetType: ''
  });

  const [specificationFields, setSpecificationFields] = useState([
    { key: '', value: '' }
  ]);

  // Load editing asset data when modal opens in edit mode
  useEffect(() => {
    if (isOpen && editingAsset) {
      // Populate form with editing asset data
      setFormData({
        assetName: editingAsset?.assetName || '',
        assetType: editingAsset?.assetType || 'computer',
        assetValue: editingAsset?.assetValue?.toString() || '',
        location: editingAsset?.location || 'in_office',
        assignedStaffMember: editingAsset?.assignedStaffMember || '',
        serialNumber: editingAsset?.serialNumber || '',
        warrantyExpiry: editingAsset?.warrantyExpiry || '',
        specifications: editingAsset?.specifications || {},
        isInsured: editingAsset?.isInsured || false,
        purchaseDate: editingAsset?.purchaseDate || '',
        notes: editingAsset?.notes || '',
        otherAssetType: editingAsset?.specifications?.customAssetType || ''
      });

      // Convert specifications object to array for editing
      const specs = editingAsset?.specifications || {};
      const specsArray = Object.entries(specs)?.filter(([key]) => key !== 'customAssetType')?.map(([key, value]) => ({ key, value }));
      setSpecificationFields(specsArray?.length > 0 ? specsArray : [{ key: '', value: '' }]);
    }
  }, [isOpen, editingAsset]);

  useEffect(() => {
    if (isOpen) {
      loadUserCurrency();
    }
  }, [isOpen]);

  const loadUserCurrency = async () => {
    try {
      const profile = await userProfileService?.getCurrentProfile();
      setUserCurrency(profile?.currency || 'USD');
    } catch (err) {
      console.error('Error loading user currency:', err);
      setUserCurrency('USD');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSpecificationChange = (index, field, value) => {
    const newSpecs = [...specificationFields];
    newSpecs[index][field] = value;
    setSpecificationFields(newSpecs);
  };

  const addSpecificationField = () => {
    setSpecificationFields([...specificationFields, { key: '', value: '' }]);
  };

  const removeSpecificationField = (index) => {
    if (specificationFields?.length > 1) {
      const newSpecs = specificationFields?.filter((_, i) => i !== index);
      setSpecificationFields(newSpecs);
    }
  };

  const resetForm = () => {
    setFormData({
      assetName: '',
      assetType: 'computer',
      assetValue: '',
      location: 'in_office',
      assignedStaffMember: '',
      serialNumber: '',
      warrantyExpiry: '',
      specifications: {},
      isInsured: false,
      purchaseDate: '',
      notes: '',
      otherAssetType: ''
    });
    setSpecificationFields([{ key: '', value: '' }]);
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validate required fields
    if (!formData?.assetName?.trim()) {
      setError('Asset name is required');
      return;
    }
    if (!formData?.assignedStaffMember?.trim()) {
      setError('Assigned staff member is required');
      return;
    }
    if (!formData?.assetValue || parseFloat(formData?.assetValue) <= 0) {
      setError('Asset value must be greater than 0');
      return;
    }

    try {
      setSaving(true);

      // Convert specifications array back to object
      const specifications = specificationFields?.reduce((acc, spec) => {
        if (spec?.key?.trim()) {
          acc[spec.key] = spec?.value;
        }
        return acc;
      }, {});

      // For "other" asset type, store the custom type in specifications
      if (formData?.assetType === 'other' && formData?.otherAssetType) {
        specifications.customAssetType = formData?.otherAssetType;
      }

      const assetData = {
        ...formData,
        specifications,
        assetValue: parseFloat(formData?.assetValue),
        currency: userCurrency
      };

      // Check if we're editing or creating
      if (editingAsset?.id) {
        await assetService?.update(editingAsset?.id, assetData);
      } else {
        await assetService?.create(assetData);
      }
      
      // Reset form and close modal
      resetForm();
      onAssetAdded?.();
      onClose?.();
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      resetForm();
      onClose?.();
    }
  };

  if (!isOpen) return null;

  const isEditMode = !!editingAsset?.id;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Asset' : 'Add New Asset'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asset Name *
                </label>
                <input
                  type="text"
                  name="assetName"
                  value={formData?.assetName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., MacBook Pro 16"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asset Type *
                </label>
                <select
                  name="assetType"
                  value={formData?.assetType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="computer">Computer</option>
                  <option value="furniture">Furniture</option>
                  <option value="equipment">Equipment</option>
                  <option value="software">Software</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Conditional field for "Other" asset type */}
              {formData?.assetType === 'other' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please specify asset type *
                  </label>
                  <input
                    type="text"
                    name="otherAssetType"
                    value={formData?.otherAssetType || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Machinery, Vehicle, etc."
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asset Value ({userCurrency}) *
                </label>
                <input
                  type="number"
                  name="assetValue"
                  value={formData?.assetValue}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">Currency from your account settings</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Staff Member *
                </label>
                <input
                  type="text"
                  name="assignedStaffMember"
                  value={formData?.assignedStaffMember}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  name="location"
                  value={formData?.location}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="in_office">In Office</option>
                  <option value="remote">Remote</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Serial Number
                </label>
                <input
                  type="text"
                  name="serialNumber"
                  value={formData?.serialNumber}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Date
                </label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={formData?.purchaseDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Warranty Expiry
                </label>
                <input
                  type="date"
                  name="warrantyExpiry"
                  value={formData?.warrantyExpiry}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isInsured"
                    checked={formData?.isInsured}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Asset is Insured
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Specifications</h3>
              <button
                type="button"
                onClick={addSpecificationField}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Field
              </button>
            </div>

            <div className="space-y-4">
              {specificationFields?.map((spec, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={spec?.key}
                    onChange={(e) => handleSpecificationChange(index, 'key', e?.target?.value)}
                    placeholder="Key (e.g., Processor)"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={spec?.value}
                      onChange={(e) => handleSpecificationChange(index, 'value', e?.target?.value)}
                      placeholder="Value (e.g., M1 Max)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {specificationFields?.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSpecificationField(index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Additional notes about this asset..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {saving ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Asset' : 'Create Asset')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}